// src/pages/Basketball.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { API_BASE } from "../api";

/* ── colour palette ────────────────────────────────────────── */
const C = {
  pageBg:     "#fff5ed",          // warm cream/peach wash
  cardBg:     "#ffffff",
  courtLine:  "#d97706",          // orange court lines
  courtKey:   "#fb923c",          // lighter orange for key/paint
  threePoint: "#ea580c",          // darker orange for 3pt arc
  accentDark: "#c2410c",          // burnt orange – headings
  accentLight:"#fed7aa",          // light orange pill / badge bg
  textPri:    "#1e293b",
  textSub:    "#475569",
  textMut:    "#94a3b8",
};

/* ── SVG: basketball court background (fixed) ──── */
function CourtBg() {
  return (
    <svg
      viewBox="0 0 800 400"
      preserveAspectRatio="xMidYMid slice"
      style={{
        position: "fixed", inset: 0, width: "100%", height: "100%",
        opacity: 0.12, pointerEvents: "none", zIndex: 0,
      }}
    >
      <rect x="40" y="40" width="720" height="320"
            fill="none" stroke={C.courtLine} strokeWidth="3" />
      <rect x="40" y="140" width="100" height="120"
            fill="none" stroke={C.courtKey} strokeWidth="2.5" />
      <circle cx="140" cy="200" r="36"
              fill="none" stroke={C.courtKey} strokeWidth="2" />
      <line x1="40" y1="180" x2="40" y2="220"
            stroke={C.courtLine} strokeWidth="4" />
      <circle cx="52" cy="200" r="9"
              fill="none" stroke={C.courtLine} strokeWidth="2.5" />
      <rect x="660" y="140" width="100" height="120"
            fill="none" stroke={C.courtKey} strokeWidth="2.5" />
      <circle cx="660" cy="200" r="36"
              fill="none" stroke={C.courtKey} strokeWidth="2" />
      <line x1="760" y1="180" x2="760" y2="220"
            stroke={C.courtLine} strokeWidth="4" />
      <circle cx="748" cy="200" r="9"
              fill="none" stroke={C.courtLine} strokeWidth="2.5" />
      <circle cx="400" cy="200" r="36"
              fill="none" stroke={C.courtLine} strokeWidth="2" />
      <line x1="400" y1="40" x2="400" y2="360"
            stroke={C.courtLine} strokeWidth="2" />
      <path d="M 40,80 Q 180,80 180,200 Q 180,320 40,320"
            fill="none" stroke={C.threePoint} strokeWidth="2" />
      <path d="M 760,80 Q 620,80 620,200 Q 620,320 760,320"
            fill="none" stroke={C.threePoint} strokeWidth="2" />
    </svg>
  );
}

export default function Basketball() {
  const [games, setGames] = useState([]);
  const [daysAhead, setDaysAhead] = useState(7);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const fetchGames = async () => {
    setLoading(true);
    setError("");
    try {
      const all = [];
      const today = new Date();
      for (let i = 0; i < daysAhead; i++) {
        const d = new Date(today);
        d.setDate(d.getDate() + i);
        const day = d.toISOString().slice(0, 10);
        const url = `${API_BASE}/api/public/fixtures/daily?day=${day}&sport=nba`;
        const r = await fetch(url, { cache: "no-store" });
        if (!r.ok) continue;
        const data = await r.json();
        if (data.fixtures) all.push(...data.fixtures);
      }
      setGames(all);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daysAhead]);

  const formatTimeUTC = (isoString) => {
    if (!isoString) return "—";
    const safe = /Z$|[+-]\d{2}:\d{2}$/.test(isoString) ? isoString : isoString + "Z";
    try {
      const d = new Date(safe);
      return d.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "UTC",
      });
    } catch {
      return isoString.slice(11, 16);
    }
  };

  return (
    <div style={{ position: "relative", minHeight: "100vh", background: C.pageBg }}>
      <CourtBg />

      <div style={{ position: "relative", zIndex: 1, maxWidth: 1200, margin: "0 auto", padding: 24 }}>
        <h2 style={{ color: C.accentDark, marginBottom: 20 }}>🏀 NBA — Upcoming Games</h2>

        <div
          style={{
            background: C.cardBg,
            borderRadius: 12,
            padding: 20,
            marginBottom: 20,
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <span style={{ color: C.textSub }}>Show next</span>
          <input
            type="number"
            min={1}
            max={30}
            value={daysAhead}
            onChange={(e) => setDaysAhead(parseInt(e.target.value || "7", 10))}
            style={{
              width: 60,
              padding: "6px 8px",
              border: `1px solid ${C.accentLight}`,
              borderRadius: 6,
              fontSize: 14,
            }}
          />
          <span style={{ color: C.textSub }}>days</span>
          <button
            onClick={fetchGames}
            style={{
              background: C.accentDark,
              color: "#fff",
              border: "none",
              padding: "6px 14px",
              borderRadius: 6,
              fontSize: 14,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Refresh
          </button>
        </div>

        {/* Odds coming soon banner */}
        <div
          style={{
            background: C.accentLight,
            border: `2px solid ${C.courtLine}`,
            borderRadius: 12,
            padding: 16,
            marginBottom: 20,
            textAlign: "center",
          }}
        >
          <p style={{ color: C.accentDark, fontWeight: 600, margin: 0 }}>
            📊 Odds coming soon! Basketball API subscription will be activated shortly.
          </p>
        </div>

        {error && <p style={{ color: C.red, marginBottom: 16 }}>{error}</p>}

        {loading ? (
          <p style={{ color: C.textSub }}>Loading...</p>
        ) : games.length === 0 ? (
          <p style={{ color: C.textMut }}>No NBA games found in the selected window.</p>
        ) : (
          <div
            style={{
              background: C.cardBg,
              borderRadius: 12,
              padding: 20,
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${C.courtLine}` }}>
                  <th align="left" style={{ padding: "10px 0", color: C.textSub, fontWeight: 600 }}>
                    Time (UTC)
                  </th>
                  <th align="left" style={{ padding: "10px 0", color: C.textSub, fontWeight: 600 }}>
                    Matchup
                  </th>
                  <th align="left" style={{ padding: "10px 0", color: C.textSub, fontWeight: 600 }}>
                    Competition
                  </th>
                  <th align="right" style={{ padding: "10px 0", color: C.textSub, fontWeight: 600 }}>
                    Best Home
                  </th>
                  <th align="right" style={{ padding: "10px 0", color: C.textSub, fontWeight: 600 }}>
                    Best Away
                  </th>
                </tr>
              </thead>
              <tbody>
                {games.map((g) => (
                  <tr
                    key={g.id}
                    style={{
                      cursor: "pointer",
                      borderBottom: `1px solid ${C.accentLight}`,
                      transition: "background 0.15s",
                    }}
                    onClick={() => navigate(`/basketball/game/${g.id}`)}
                    onMouseEnter={(e) => (e.currentTarget.style.background = C.accentLight + "40")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  >
                    <td style={{ padding: "12px 0", color: C.textPri }}>{formatTimeUTC(g.kickoff_utc)}</td>
                    <td style={{ padding: "12px 0" }}>
                      <Link
                        to={`/basketball/game/${g.id}`}
                        onClick={(e) => e.stopPropagation()}
                        style={{ display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none", color: "inherit" }}
                      >
                        {g.home_logo && (
                          <img
                            src={g.home_logo}
                            alt={g.home_team}
                            width={22}
                            height={22}
                            style={{ objectFit: "contain" }}
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                        )}
                        <strong style={{ color: C.textPri }}>{g.home_team}</strong>
                        <span style={{ opacity: 0.5, color: C.textMut }}>vs</span>
                        {g.away_logo && (
                          <img
                            src={g.away_logo}
                            alt={g.away_team}
                            width={22}
                            height={22}
                            style={{ objectFit: "contain" }}
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                        )}
                        <span style={{ color: C.textPri }}>{g.away_team}</span>
                      </Link>
                    </td>
                    <td style={{ padding: "12px 0", color: C.textSub }}>{g.comp || "NBA"}</td>
                    <td align="right" style={{ padding: "12px 0", color: C.textPri }}>
                      {g.best_home ? `${g.best_home.price} (${g.best_home.bookmaker})` : "—"}
                    </td>
                    <td align="right" style={{ padding: "12px 0", color: C.textPri }}>
                      {g.best_away ? `${g.best_away.price} (${g.best_away.bookmaker})` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
